// Campo de telefone com seletor de país (bandeira + prefixo discável).
//
// Guarda o valor em E.164 ("+55..."), formato exigido pelo WhatsApp/Twilio.
// Favoritos aparecem sempre; "Ver mais" abre a lista completa com busca.
// [[feedback Luís 2026-05-27: guardar com prefixo do país]]

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Check, ChevronDown, Search } from "lucide-react";
import {
  type Country,
  allCountriesSorted,
  favoriteCountries,
  parseStoredPhone,
  toE164
} from "./lib/countries";

export function PhoneField({
  value,
  onChange
}: {
  value: string;
  onChange: (e164: string) => void;
}) {
  // Estado inicial derivado do valor salvo (uma vez). Depois o componente é a
  // fonte e emite onChange — não re-sincroniza de props pra evitar loop.
  const initial = useMemo(() => parseStoredPhone(value), []);
  const [country, setCountry] = useState<Country>(initial.country);
  const [national, setNational] = useState<string>(initial.national);
  const [open, setOpen] = useState(false);

  function emit(nextCountry: Country, nextNational: string) {
    onChange(toE164(nextCountry.dial, nextNational));
  }

  function pickCountry(c: Country) {
    setCountry(c);
    setOpen(false);
    emit(c, national);
  }

  function onNationalInput(e: Event) {
    // Só dígitos no campo nacional — o "+" e o país vêm do seletor.
    const digits = (e.target as HTMLInputElement).value.replace(/\D/g, "");
    setNational(digits);
    emit(country, digits);
  }

  return (
    <label class="block">
      <span class="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Telefone (WhatsApp / SMS)
      </span>
      <div class="flex items-stretch gap-1.5">
        <CountryPicker
          country={country}
          open={open}
          onToggle={() => setOpen((v) => !v)}
          onClose={() => setOpen(false)}
          onPick={pickCountry}
        />
        <input
          type="tel"
          inputMode="numeric"
          value={national}
          onInput={onNationalInput}
          aria-label="Número de telefone"
          placeholder={country.iso2 === "BR" ? "11999999999" : "número"}
          class="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </div>
      <span class="mt-1 block text-[10px] text-muted-foreground">
        {national
          ? `Será salvo como ${toE164(country.dial, national)}`
          : "Usado para lembretes por WhatsApp e SMS."}
      </span>
    </label>
  );
}

function CountryPicker({
  country,
  open,
  onToggle,
  onClose,
  onPick
}: {
  country: Country;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onPick: (c: Country) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState("");

  // Click-fora e ESC fecham o dropdown.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // Reseta o estado interno toda vez que abre (volta pros favoritos).
  useEffect(() => {
    if (open) {
      setShowAll(false);
      setQuery("");
    }
  }, [open]);

  const favorites = useMemo(() => favoriteCountries(), []);
  const all = useMemo(() => allCountriesSorted(), []);
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return all;
    return all.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dial.includes(q.replace(/\D/g, "")) ||
        c.iso2.toLowerCase().includes(q)
    );
  }, [all, q]);

  return (
    <div ref={ref} class="relative shrink-0">
      <button
        type="button"
        onClick={onToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`País: ${country.name} (+${country.dial})`}
        title={`${country.name} (+${country.dial})`}
        class="flex h-full items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none transition-colors hover:border-primary focus:border-primary"
      >
        <span class="text-base leading-none">{country.flag}</span>
        <span class="font-medium">+{country.dial}</span>
        <ChevronDown className="size-3.5 text-muted-foreground" />
      </button>

      {open && (
        <div class="absolute left-0 top-full z-50 mt-1 w-72 max-w-[80vw] overflow-hidden rounded-md border border-border bg-popover shadow-xl">
          {!showAll ? (
            <>
              <div class="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Mais usados
              </div>
              <CountryList
                items={favorites}
                selectedIso2={country.iso2}
                onPick={onPick}
              />
              <button
                type="button"
                onClick={() => setShowAll(true)}
                class="flex w-full items-center justify-center gap-1.5 border-t border-border px-3 py-2 text-xs font-semibold text-primary transition-colors hover:bg-accent"
              >
                Ver mais países
                <ChevronDown className="size-3.5" />
              </button>
            </>
          ) : (
            <>
              <div class="relative border-b border-border p-2">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  autoFocus
                  value={query}
                  onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
                  placeholder="Buscar país ou prefixo..."
                  aria-label="Buscar país"
                  class="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-2 text-sm outline-none focus:border-primary"
                />
              </div>
              <div class="max-h-64 overflow-y-auto">
                {filtered.length === 0 ? (
                  <p class="px-3 py-4 text-center text-xs text-muted-foreground">
                    Nenhum país encontrado.
                  </p>
                ) : (
                  <CountryList
                    items={filtered}
                    selectedIso2={country.iso2}
                    onPick={onPick}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CountryList({
  items,
  selectedIso2,
  onPick
}: {
  items: Country[];
  selectedIso2: string;
  onPick: (c: Country) => void;
}) {
  return (
    <ul role="listbox" class="max-h-64 overflow-y-auto py-1">
      {items.map((c) => {
        const active = c.iso2 === selectedIso2;
        return (
          <li key={c.iso2}>
            <button
              type="button"
              role="option"
              aria-selected={active}
              onClick={() => onPick(c)}
              class={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-accent ${
                active ? "bg-accent/60 font-semibold" : ""
              }`}
            >
              <span class="text-base leading-none">{c.flag}</span>
              <span class="min-w-0 flex-1 truncate">{c.name}</span>
              <span class="text-xs text-muted-foreground">+{c.dial}</span>
              {active && <Check className="size-3.5 text-primary" />}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
